import React from 'react'
import { styled } from '@mui/material/styles'
import { Box, Paper, Typography } from '@mui/material'

// Layout helpers
export const FlexRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
})

export const FlexBetween = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
})

export const FlexCenter = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
})

// Code display
export const CodeBlock = styled('code')(({ theme }) => ({
  backgroundColor: theme.palette.grey[100],
  padding: '2px 8px',
  borderRadius: 4,
  fontSize: '0.75rem',
  fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
  display: 'inline-block',
}))

// Form container
export const FormCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  maxWidth: 600,
}))

// Detail section container
export const DetailSection = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
}))

// Icon circle for empty states
export const IconCircle = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  padding: theme.spacing(2),
  borderRadius: '50%',
  backgroundColor: theme.palette.grey[100],
  marginBottom: theme.spacing(2),
}))

// Page header component
interface PageHeaderProps {
  title: string
  description?: string
  action?: React.ReactNode
  breadcrumb?: React.ReactNode
}

export function PageHeader({ title, description, action, breadcrumb }: PageHeaderProps) {
  return (
    <Box sx={{ mb: 4 }}>
      {breadcrumb && <Box sx={{ mb: 1 }}>{breadcrumb}</Box>}
      <FlexBetween>
        <Box>
          <Typography variant="h5" fontWeight={600}>
            {title}
          </Typography>
          {description && (
            <Typography variant="body2" color="text.secondary">
              {description}
            </Typography>
          )}
        </Box>
        {action && <Box>{action}</Box>}
      </FlexBetween>
    </Box>
  )
}

// Section header for detail pages
interface SectionHeaderProps {
  title: string
  action?: React.ReactNode
}

export function SectionHeader({ title, action }: SectionHeaderProps) {
  return (
    <FlexBetween sx={{ mb: 2 }}>
      <Typography variant="h6">{title}</Typography>
      {action}
    </FlexBetween>
  )
}

// Detail row for key-value displays
interface DetailRowProps {
  label: string
  children: React.ReactNode
}

export function DetailRow({ label, children }: DetailRowProps) {
  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
        {label}
      </Typography>
      <Box>{children}</Box>
    </Box>
  )
}

// Main content wrapper
export const MainContent = styled(Box)(({ theme }) => ({
  maxWidth: 1280,
  marginLeft: 'auto',
  marginRight: 'auto',
}))

// Sidebar navigation item styles (for theme)
export const sidebarStyles = {
  drawer: {
    width: 256,
    flexShrink: 0,
    '& .MuiDrawer-paper': {
      width: 256,
      boxSizing: 'border-box',
      bgcolor: '#1a1a1a',
      color: 'white',
    },
  },
  navItem: {
    mb: 0.5,
    color: 'grey.300',
    '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' },
    '&.Mui-selected': {
      bgcolor: 'rgba(255, 255, 255, 0.1)',
      '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.15)' },
    },
  },
  navIcon: {
    color: 'grey.400',
    minWidth: 40,
  },
}

// Auth layout container
export const AuthContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: theme.palette.background.default,
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(4),
}))

// Table action buttons container
export const TableActions = styled(Box)({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: 4,
})
